import React, { useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AnyAri } from '@atlassian/ari/any-ari';
import { useServiceCore as useService } from '@atlassian/expansion-services/core';
import { type FetchInput, invite as inviteService } from '@atlassian/expansion-services/services';
import { useInvisibleRecaptcha } from '@atlassian/google-recaptcha';

/* fetchJson is temporary until we import from adapter */

import { GROWTH_ANALYTICS_CHANNEL } from '../common/ui/analytics/constants';

import { InlineInviteComponent } from './inline-invite-component';
import { getRecaptchaSiteKey } from './inline-invite-component/utils';
import type { InviteInviteProps } from './types';

const fetchJson = async (input: FetchInput) => {
	const url = input.url;
	const options = input;

	try {
		const response = await fetch(url, options);
		if (response.ok) {
			return response.json();
		} else {
			throw new Error(`Fetch failed with status ${response.status}`);
		}
	} catch (error) {
		if (error instanceof Error) {
			error.message = `Error while executing fetch for ${url}: ${error.message}`;
		}
		throw error;
	}
};

export const InlineInvite = (props: InviteInviteProps): React.JSX.Element => {
	const { atlassianAccountId, cloudId, product } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isRecaptchaLoaded, getRecaptchaToken } = useInvisibleRecaptcha({
		siteKey: getRecaptchaSiteKey(),
		displayBadge: false,
		analyticsChannel: GROWTH_ANALYTICS_CHANNEL,
		analyticsSource: `InlineInvite_${props.integration}`,
	});

	const deps = useMemo(
		() => ({
			createAnalyticsEvent,
			fetchJSON: fetchJson,
			getCurrentCloudId: () => cloudId,
			getCurrentUserId: () => atlassianAccountId,
			getCurrentProduct: () => product,
			// Should we use dependency injection for getRecaptchaToken?
		}),
		[createAnalyticsEvent, cloudId, atlassianAccountId, product],
	);

	const invite = useService(inviteService, deps);

	const resources = [
		AnyAri.create({
			resourceOwner: product,
			resourceType: 'site',
			resourceId: cloudId,
		}),
	];

	return (
		<InlineInviteComponent
			{...props}
			invite={invite}
			resources={resources}
			isRecaptchaLoaded={isRecaptchaLoaded}
			getRecaptchaToken={getRecaptchaToken}
		/>
	);
};
